import React, { useEffect, useState, useCallback } from 'react';

function PmsExpenseRoleTemplate({ token, selectedProperty }) {
  const [templates, setTemplates] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [selectedUser, setSelectedUser] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [applying, setApplying] = useState(false);

  const headers = (extra = {}) => ({
    Authorization: `Bearer ${token}`,
    'x-chain-id': selectedProperty?.chain_id || '',
    'x-property-id': selectedProperty?.id || '',
    ...extra
  });

  const loadData = useCallback(async () => {
    if (!selectedProperty?.id) return;
    try {
      const [tRes, uRes] = await Promise.all([
        fetch('/api/v1/expense/role-templates', { headers: headers() }),
        fetch('/api/users', { headers: headers() })
      ]);
      const tData = await tRes.json();
      const uData = await uRes.json();
      if (tRes.ok) setTemplates(tData.rows || []);
      if (uRes.ok) setUsers(Array.isArray(uData) ? uData : (uData.rows || []));
      setError('');
    } catch { setError('Network error'); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, selectedProperty?.id]);

  useEffect(() => { loadData(); }, [loadData]);

  const apply = async () => {
    if (!selectedTemplate || !selectedUser) return setError('Pick both template and user');
    setApplying(true);
    setError('');
    setMessage('');
    try {
      const res = await fetch(`/api/v1/expense/role-templates/${selectedTemplate}/apply-to-user/${selectedUser}`, {
        method: 'POST',
        headers: headers({ 'Content-Type': 'application/json' }),
        body: '{}'
      });
      const data = await res.json();
      if (!res.ok) return setError(data.error || 'Apply failed');
      setMessage(`Applied ${data.applied} flag(s) to user. They'll see new permissions on next login.`);
    } catch { setError('Network error'); }
    finally { setApplying(false); }
  };

  const renderBundle = (bundle) => {
    if (!bundle || typeof bundle !== 'object') return '—';
    return Object.entries(bundle)
      .filter(([, v]) => v === true)
      .map(([k]) => k.replace(/^can_/, ''))
      .join(', ') || '—';
  };

  const inputStyle = { padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.9rem' };

  return (
    <div className="page-shell">
      <h2 style={{ color: 'var(--theme-primary-dark)', marginBottom: '0.8rem' }}>Role Templates</h2>
      {!selectedProperty?.id && <div className="error-banner">Select a property first.</div>}
      {error && <div style={{ background: '#fee2e2', color: '#991b1b', padding: '0.6rem 1rem', borderRadius: 6, marginBottom: '0.8rem' }}>{error}</div>}
      {message && <div style={{ background: '#dcfce7', color: '#166534', padding: '0.6rem 1rem', borderRadius: 6, marginBottom: '0.8rem' }}>{message}</div>}
      <p style={{ color: '#6b7280', fontSize: '0.85rem' }}>Role templates are pre-built bundles of expense-related permission flags. Apply a template to a user to grant them the flags in that bundle.</p>

      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: '1rem', marginBottom: '0.8rem' }}>
        <h3 style={{ margin: '0 0 0.7rem 0', fontSize: '1rem' }}>Apply Template to User</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '0.7rem' }}>
          <label style={{ display: 'grid', gap: '0.25rem', fontSize: '0.85rem' }}>
            <span>Template</span>
            <select value={selectedTemplate} onChange={e => setSelectedTemplate(e.target.value)} style={inputStyle}>
              <option value="">— Select template —</option>
              {templates.map(t => <option key={t.template_id} value={t.template_id}>{t.template_name}</option>)}
            </select>
          </label>
          <label style={{ display: 'grid', gap: '0.25rem', fontSize: '0.85rem' }}>
            <span>User</span>
            <select value={selectedUser} onChange={e => setSelectedUser(e.target.value)} style={inputStyle}>
              <option value="">— Select user —</option>
              {users.map(u => <option key={u.id} value={u.id}>{u.username || u.email}</option>)}
            </select>
          </label>
          <div style={{ alignSelf: 'end' }}>
            <button onClick={apply} disabled={applying} style={{ padding: '0.55rem 1.2rem', border: 'none', borderRadius: 6, background: '#2563eb', color: '#fff', cursor: 'pointer', fontWeight: 600 }}>
              {applying ? 'Applying…' : 'Apply Template'}
            </button>
          </div>
        </div>
      </div>

      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
        <div style={{ padding: '0.6rem 1rem', borderBottom: '1px solid #e5e7eb', background: '#fafafa', fontWeight: 600 }}>Available Templates</div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
          <thead>
            <tr style={{ background: '#f3f4f6', textAlign: 'left' }}>
              <th style={{ padding: '0.55rem' }}>Name</th>
              <th style={{ padding: '0.55rem' }}>Description</th>
              <th style={{ padding: '0.55rem' }}>Flags Granted</th>
              <th style={{ padding: '0.55rem' }}>Source</th>
            </tr>
          </thead>
          <tbody>
            {templates.length === 0 ? (
              <tr><td colSpan={4} style={{ padding: '1.5rem', textAlign: 'center', color: '#6b7280' }}>No templates available. Run the seeder from Admin.</td></tr>
            ) : templates.map(t => (
              <tr key={t.template_id} style={{ borderTop: '1px solid #f3f4f6' }}>
                <td style={{ padding: '0.5rem', fontWeight: 600 }}>{t.template_name}</td>
                <td style={{ padding: '0.5rem', color: '#6b7280' }}>{t.description || '—'}</td>
                <td style={{ padding: '0.5rem', fontFamily: 'monospace', fontSize: '0.78rem' }}>{renderBundle(t.flag_bundle)}</td>
                <td style={{ padding: '0.5rem' }}>{t.is_seeded ? <span style={{ fontSize: '0.72rem', padding: '1px 6px', borderRadius: 4, background: '#e0e7ff', color: '#3730a3' }}>Seeded</span> : 'Custom'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default PmsExpenseRoleTemplate;
